// src/components/DetailedListView.js
import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom'; // Added Link for potential future use
import { formatDate, formatDateTime } from '../utils'; // Assuming these are in utils
import {
    // Import all relevant status/severity options from your constants file
    GOAL_STATUS_OPTIONS,
    INITIATIVE_STATUS_OPTIONS,
    ACTION_ITEM_STATUS_OPTIONS,
    ISSUE_STATUS_OPTIONS,
    ISSUE_SEVERITY_OPTIONS,
    // Add any other specific option lists you have
    GENERIC_STATUS_OPTIONS // Fallback
} from '../constants'; // Adjust path if necessary

// --- Helper Components ---

// Utility to format display values (remains the same)
const formatDisplayValue = (value, formatter) => {
  if (formatter && typeof formatter === 'function') {
    try {
      const formatted = formatter(value);
      return formatted === null || typeof formatted === 'undefined' ? <span className="text-gray-400 italic">N/A</span> : formatted;
    } catch (e) {
      console.error("Error in formatter:", e);
      return <span className="text-red-500 italic">Format Error</span>;
    }
  }
  if (value === null || typeof value === 'undefined') {
    return <span className="text-gray-400 italic">N/A</span>;
  }
  if (Array.isArray(value)) {
    const filtered = value.filter(item => item !== null && typeof item !== 'undefined');
    return filtered.length > 0 ? filtered.join(', ') : <span className="text-gray-400 italic">None</span>;
  }
  return String(value);
};

// Component to display truncated text (remains the same)
const LongTextView = ({ text, maxLength = 100 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!text) return <span className="text-gray-400 italic">No description</span>;
  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  if (text.length <= maxLength || isExpanded) {
    return (
      <div className="whitespace-pre-wrap text-gray-800">
        {text}
        {text.length > maxLength && (
          <button onClick={toggleExpand} className="text-blue-600 hover:underline ml-2 text-xs font-medium">(Show Less)</button>
        )}
      </div>
    );
  }
  return (
    <div className="text-gray-800">
      {text.substring(0, maxLength)}...
      <button onClick={toggleExpand} className="text-blue-600 hover:underline ml-2 text-xs font-medium">(Show More)</button>
    </div>
  );
};

// --- Main Component ---
const DetailedListView = ({
    title,
    items,
    columns,
    isLoading,
    error,
    entityType,
    // New props for inline editing
    neo4jService,   // Pass the service instance
    clientId,       // Pass current client ID
    onItemUpdate,   // Callback to refresh parent list: (itemId, updatedField, newValue) => void
    editableColumnsConfig // Object defining editable columns: { status: { options: [], queryKey: 'updateNodeStatus', propertyName: 'status' }, ... }
  }) => {
  const navigate = useNavigate();
  // We get clientId from props now, but useParams() might still be useful if this component could be routed directly.
  // const { clientId: routeClientId } = useParams(); 
  // const currentClientId = clientId || routeClientId; // Prefer prop if available

  const [updatingItemId, setUpdatingItemId] = useState(null); // ID of item being updated
  const [updateError, setUpdateError] = useState(null);

  const handleRowClick = (item) => {
    if (updatingItemId === item.id) return; // Don't navigate if an update for this row is in progress via dropdown
    if (!entityType || !item.id) {
        console.error("Cannot navigate: entityType or item.id missing", { entityType, itemId: item?.id });
        return;
    }
    
    // Check if item has its own clientId (for department views)
    const itemClientId = item.clientId || item.client_id;
    
    if (itemClientId) {
        // Navigate to client context if item has a clientId
        navigate(`/clients/${itemClientId}/${entityType}/${item.id}`);
    } else if (clientId) {
        // Use context clientId if available
        navigate(`/clients/${clientId}/${entityType}/${item.id}`);
    } else {
        // No client context available
        console.warn(`Cannot navigate: No client ID available for item ${item.id}`);
    }
};

  const handleInlineUpdate = async (item, columnKey, newValue) => {
    if (!neo4jService || !clientId || !editableColumnsConfig || !editableColumnsConfig[columnKey]) {
        console.error("Inline update prerequisites not met.", {neo4jService, clientId, editableColumnsConfig, columnKey});
        setUpdateError("Cannot perform update: Configuration missing.");
        return;
    }

    const config = editableColumnsConfig[columnKey];
    if (!config.query || !config.propertyName) {
        console.error("Editable column config missing query or propertyName for key:", columnKey);
        setUpdateError(`Update configuration error for ${columnKey}.`);
        return;
    }
    
    setUpdatingItemId(item.id);
    setUpdateError(null);

    try {
        // The updateQueries in graphQueries.js should handle the specific query logic
        await neo4jService.runQuery(config.query, {
            nodeId: item.id,
            clientId: clientId,
            newValue: newValue
            // propertyName: config.propertyName // Only needed if using a generic updateNodeProperty query
        });

        if (onItemUpdate) {
            onItemUpdate(item.id, config.propertyName, newValue); // Notify parent to refresh or update its state
        }
        // Optionally, show a success message briefly
    } catch (err) {
        console.error(`Failed to update ${config.propertyName} for item ${item.id}:`, err);
        setUpdateError(`Failed to update. ${err.message}`);
        // Optionally, revert optimistic update if one was made locally
    } finally {
        setUpdatingItemId(null);
    }
  };


  if (isLoading) { return (<div className="bg-white rounded-lg shadow p-6 mt-8"><h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3><div className="text-center py-4 text-gray-500">Loading data...</div></div>); }
  if (error) { return (<div className="bg-white rounded-lg shadow p-6 mt-8"><h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3><div className="text-red-600 py-4 text-center">Error loading data: {error}</div></div>); }
  if (!items || items.length === 0) { return (<div className="bg-white rounded-lg shadow p-6 mt-8"><h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3><div className="text-center text-gray-500 py-4">No items found.</div></div>); }

  return (
    <div className="bg-white rounded-lg shadow p-4 md:p-6 mt-6 md:mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        {updateError && <div className="mb-2 p-2 text-sm bg-red-100 text-red-700 rounded">Update Error: {updateError}</div>}
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border-collapse">
                <thead className="bg-gray-50">
                    <tr>
                        {columns.map((col) => (
                            <th key={col.key} scope="col" className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.headerClassName || ''}`} style={col.align ? { textAlign: col.align } : {}}>
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => (
                        <tr
                            key={item.id}
                            className={`hover:bg-gray-50 ${entityType ? 'cursor-pointer' : ''}`}
                            onClick={() => entityType ? handleRowClick(item) : undefined}
                        >
                            {columns.map((col) => {
                                const isEditable = editableColumnsConfig && editableColumnsConfig[col.key];
                                const currentItemValue = item[col.key];

                                return (
                                    <td key={`${item.id}-${col.key}`} className={`px-3 py-3 whitespace-normal text-sm align-top ${col.cellClassName || ''}`}>
                                        {isEditable ? (
                                            <select
                                                value={currentItemValue || ''}
                                                onChange={(e) => {
                                                    e.stopPropagation(); // Important: Prevent row click
                                                    handleInlineUpdate(item, col.key, e.target.value);
                                                }}
                                                onClick={(e) => e.stopPropagation()} // Prevent row click on dropdown itself
                                                disabled={updatingItemId === item.id}
                                                className={`p-1 border rounded text-sm w-full ${updatingItemId === item.id ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 bg-white hover:border-gray-400'}`}
                                            >
                                                {/* Add a default "current" option if value is not in standard list (e.g. old status) */}
                                                {currentItemValue && !editableColumnsConfig[col.key].options.includes(currentItemValue) && (
                                                    <option value={currentItemValue} disabled>{String(currentItemValue).charAt(0).toUpperCase() + String(currentItemValue).slice(1)} (Current)</option>
                                                )}
                                                {editableColumnsConfig[col.key].options.map(option => (
                                                    <option key={option} value={option}>
                                                        {String(option).charAt(0).toUpperCase() + String(option).slice(1)}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : col.key === 'description' || col.type === 'longText' ? (
                                            <LongTextView text={currentItemValue} />
                                        ) : (
                                            <span className="text-gray-800">{formatDisplayValue(currentItemValue, col.formatter)}</span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
    );
  };

export default DetailedListView;
